import type {
  ModelGroup,
  ModelRelease,
  ReleaseGroupView,
  TimelineRelease
} from "../types/model";

const GROUP_ENTRY_PREFIX = "group:";
// Group IDs never contain a slash, so the first one starts the category.
const CATEGORY_PART_SEPARATOR = "/";

/** A group's card, or one category's part of a group split by category. */
export function groupEntryId(groupId: string, category?: string): string {
  return category === undefined
    ? `${GROUP_ENTRY_PREFIX}${groupId}`
    : `${GROUP_ENTRY_PREFIX}${groupId}${CATEGORY_PART_SEPARATOR}${category}`;
}

export function parseGroupEntryId(
  id: string
): { groupId: string; category?: string } | null {
  if (!id.startsWith(GROUP_ENTRY_PREFIX)) return null;
  const rest = id.slice(GROUP_ENTRY_PREFIX.length);
  const at = rest.indexOf(CATEGORY_PART_SEPARATOR);
  return at < 0
    ? { groupId: rest }
    : { groupId: rest.slice(0, at), category: rest.slice(at + 1) };
}

/**
 * How many models a timeline card stands for: the members of a group card that
 * match the current search and filters, else one. Section counts and the
 * model-count sort use it, so folding never lowers a model count.
 */
export function entryModelCount(entry: TimelineRelease): number {
  return entry.groupView?.matchedIds.length ?? 1;
}

function byEarliestRelease(left: ModelRelease, right: ModelRelease): number {
  return (
    (left.dates.start ?? "9999").localeCompare(right.dates.start ?? "9999") ||
    left.name.localeCompare(right.name)
  );
}

/**
 * Index every record of every release group, earliest release first. Groups
 * with one member are kept: they render as that member's ordinary card.
 */
export function indexGroupMembers(
  records: readonly ModelRelease[],
  groups: readonly ModelGroup[]
): Map<string, ModelRelease[]> {
  const known = new Set(groups.map((group) => group.id));
  const members = new Map<string, ModelRelease[]>();
  for (const record of records) {
    const groupId = record.modelGroupId;
    if (!groupId || !known.has(groupId)) continue;
    const current = members.get(groupId);
    if (current) current.push(record);
    else members.set(groupId, [record]);
  }
  for (const list of members.values()) list.sort(byEarliestRelease);
  return members;
}

/**
 * Fold the records that match the current query into timeline cards. A group
 * with two or more members becomes one card as soon as any member matches; the
 * card takes the latest member's fields, so date grouping and sorting place
 * the group at its newest release, and carries every member in `groupView`.
 *
 * Under the category grouping (`byCategory`), a group whose members sit in
 * different categories splits into one part per category, so every member
 * stays under its own category (owner decision 2026-10-29). A part with two or
 * more members is still one card; a part with one member is that member's
 * ordinary card.
 */
export function foldReleaseGroups(
  matched: readonly ModelRelease[],
  allRecords: readonly ModelRelease[],
  groups: readonly ModelGroup[],
  { byCategory = false }: { byCategory?: boolean } = {}
): TimelineRelease[] {
  if (!groups.length) return [...matched];
  const groupById = new Map(groups.map((group) => [group.id, group]));
  const parts = new Map<string, ModelRelease[]>();
  for (const [groupId, members] of indexGroupMembers(allRecords, groups)) {
    const categories = new Set(members.map((member) => member.mainCategory));
    if (byCategory && categories.size > 1) {
      for (const category of categories) {
        parts.set(
          groupEntryId(groupId, category),
          members.filter((member) => member.mainCategory === category)
        );
      }
    } else {
      parts.set(groupEntryId(groupId), members);
    }
  }
  const partIdOf = (record: ModelRelease) => {
    if (!record.modelGroupId) return undefined;
    const whole = groupEntryId(record.modelGroupId);
    return parts.has(whole)
      ? whole
      : groupEntryId(record.modelGroupId, record.mainCategory);
  };
  const matchedByPart = new Map<string, string[]>();
  for (const record of matched) {
    const partId = partIdOf(record);
    if (!partId) continue;
    const current = matchedByPart.get(partId);
    if (current) current.push(record.id);
    else matchedByPart.set(partId, [record.id]);
  }

  const emitted = new Set<string>();
  const entries: TimelineRelease[] = [];
  for (const record of matched) {
    const partId = partIdOf(record);
    const group = record.modelGroupId ? groupById.get(record.modelGroupId) : undefined;
    const members = partId ? parts.get(partId) : undefined;
    if (!partId || !group || !members || members.length < 2) {
      entries.push(record);
      continue;
    }
    if (emitted.has(partId)) continue;
    emitted.add(partId);
    entries.push(
      buildGroupEntry(group, members, matchedByPart.get(partId) ?? [], partId)
    );
  }
  return entries;
}

export function buildGroupEntry(
  group: ModelGroup,
  members: ModelRelease[],
  matchedIds: string[],
  id = groupEntryId(group.id)
): TimelineRelease {
  // Members are kept earliest first, and a member with no date sorts last.
  const anchor =
    members.findLast((member) => member.dates.start) ?? members[members.length - 1]!;
  return {
    ...anchor,
    id,
    name: group.name,
    modelGroupId: group.id,
    groupView: { group, members, matchedIds }
  };
}

/**
 * Whether the group panel marks its members: true as soon as the search or
 * filters leave any member out, including all of them. A panel no member
 * matches stays open (App keeps it for the reader) and dims every card.
 */
export function groupMembersFiltered(view: ReleaseGroupView): boolean {
  return view.matchedIds.length < view.members.length;
}
